// reactjs imports
import { useEffect, useRef, useState } from "react";

// firebase imports
import { getAuth } from "firebase/auth";
import { app } from "../firebase/firebase-init";

// component imports
import Header from "../component/Header";
import Loading from "../component/Loading";

const ApplicantDetails = ({ student }) => {
  const titles = [
    "Full Name",
    "Email",
    "Contact",
    "Batch",
    "Course Type",
    "Course",
    "Guardian Contact",
    "Father's Name",
    "Mother's Name",
    "Date of Birth",
    "Designation",
    "Organization",
    "Occupation",
    "Currently Working At",
    "Working Sector",
    "Last Education Qualification",
    "NID/B. Reg. No.",
    "Permanent Address",
    "Present Address",
    "Gender",
  ];

  const dataList = [
    student.fullName,
    student.email,
    student.contact,
    student.batch,
    student.courseType?.toUpperCase(),
    student.course?.toUpperCase(),
    student.guardianContact,
    student.fatherName,
    student.motherName,
    student.dateOfBirth ? new Date(student.dateOfBirth).toDateString() : null,
    student.designation,
    student.organization,
    student.occupation,
    student.currentlyWorking,
    student.workingSector,
    student.lastEducationQualification,
    student.id,
    student.permanentAddress,
    student.presentAddress,
    student.gender,
  ];

  function notAvailableString(data) {
    return data || "N/A";
  }

  return (
    <>
      <h5 className="px-2 py-1 border-bottom border-secondary">
        Applicant Details
      </h5>

      <table className="table table-bordered">
        <tbody>
          {titles.map((title, index) => {
            return (
              <tr key={index}>
                <td>
                  <strong className="text-break text-wrap text-capitalize">
                    {title}
                  </strong>
                </td>

                <td>
                  <span className="text-break text-wrap text-capitalize">
                    {notAvailableString(dataList[index])}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
};

const Content = ({ ID }) => {
  const [student, setStudent] = useState(null);

  const componentRef = useRef(null);

  const handleDownload = () => {
    const elementToPrint = componentRef.current;
    const clonedElement = elementToPrint.cloneNode(true);

    clonedElement.firstChild.classList.contains("col-md-3") &&
      clonedElement.firstChild.classList.add("col-3");

    const printWindow = window.open("", "_blank");

    printWindow.document.write(
      `<html><head><title>${student.fullName} Details</title>`
    );
    printWindow.document.write(
      '<link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0-alpha3/dist/css/bootstrap.min.css" rel="stylesheet" integrity="sha384-KK94CHFLLe+nY2dmCWGMq91rCGa5gtU4mk92HdvYe+M/SXH301p5ILy+dN9+nJOZ" crossorigin="anonymous">'
    );
    printWindow.document.write("</head><body>");
    printWindow.document.write(
      `<div id="print-content" class="row">${clonedElement.innerHTML}</div>`
    );
    printWindow.document.write(
      `<script type="text/javascript">
        window.onload = function() {
          window.print();
          window.onafterprint = function() {
            window.close();
          };
        }
      </script>`
    );
    printWindow.document.write("</body></html>");
    printWindow.document.close();
  };

  useEffect(() => {
    async function getStudent() {
      try {
        const { doc, getDoc } = await import("firebase/firestore/lite");
        const { db } = await import("../firebase/firebase-init");

        const docRef = doc(db, "applications", ID);

        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          setStudent(docSnap.data());
        } else {
          window.location.href = "/";
        }
      } catch (error) {
        console.log(error);
        alert("Something went wrong. Please reload the page");
      }
    }

    getStudent();

    return () => {
      setStudent(null);
    };
  }, [ID]);

  return (
    <>
      <Header admin />

      <div className="container">
        {student && (
          <button
            type="button"
            onClick={handleDownload}
            className="btn btn-primary position-fixed bottom-0 end-0 m-3 p-3 d-flex align-items-center justify-content-center text-center rounded-circle">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              fill="currentColor"
              className="bi bi-arrow-down-circle"
              viewBox="0 0 16 16">
              <path
                fillRule="evenodd"
                d="M1 8a7 7 0 1 0 14 0A7 7 0 0 0 1 8zm15 0A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM8.5 4.5a.5.5 0 0 0-1 0v5.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V4.5z"
              />
            </svg>

            <span className="visually-hidden">download pdf</span>
          </button>
        )}

        <div
          ref={componentRef}
          className="row mx-auto my-3 p-3 px-2 gap-4 h-50 border border-2 rounded-1 border-black">
          {student?.photo && (
            <div className={`col-md-3 ${student ? `` : `placeholder-wave`}`}>
              <img
                style={{
                  objectPosition: "top",
                  minHeight: "100px",
                  maxHeight: "300px",
                }}
                className="w-100 object-fit-contain"
                src={student.photo}
                alt={student.fullName}
              />
            </div>
          )}

          <div className={`col ${student ? `` : `placeholder-wave`}`}>
            {student ? (
              <ApplicantDetails student={student} />
            ) : (
              <div className="placeholder col-12 h-100 p-5 rounded-1"></div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

const Student = () => {
  const param = new URLSearchParams(window.location.search);
  const ID = param.get("ID");
  if (!ID) {
    window.location.href = "/";
  }

  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    const auth = getAuth(app);

    auth.onAuthStateChanged((user) => {
      setIsLoggedIn(user);

      if (!user) {
        window.location.href = "/login";
      }
    });
  }, []);

  return isLoggedIn ? (
    <Content ID={ID} />
  ) : isLoggedIn === false ? (
    <Loading />
  ) : (
    `Logged Out. Redirecting...`
  );
};

export default Student;
